import { useMemo, useState } from 'react';
import { Container } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import { DataGrid } from '@mui/x-data-grid';
import isMobile from 'is-mobile';
import { BRL_QUOTE_TICKER } from '../../constants';
import { getStockColumns, toEditingFavorite } from './columns';
import { DividendProjectionChart } from './components/DividendProjectionChart';
import { EditPositionModal } from './components/EditPositionModal';
import { StockCard } from './components/StockCard';
import { WalletTotal } from './components/WalletTotal';
import { useFavorites } from './hooks/useFavorites';
import { useGetForecast } from './hooks/useGetForecast';
import { useGetQuotes } from './hooks/useGetQuotes';
import { Favorite } from './types';
import { buildStockRows, getForecastYears, parseDividendYield } from './utils/stockRows';
import { calculateWalletSummary } from './utils/walletSummary';

type StockTableProps = {
  /** Mostra só os ativos da carteira, com resumo, gráficos e edição de quantidade/meta. */
  walletOnly?: boolean;
};

function StockTable({ walletOnly = false }: StockTableProps) {
  const { quotes } = useGetQuotes();
  const { data: forecast } = useGetForecast(!!quotes && !!Object.keys(quotes).length);
  const { favorites, isFavorite, toggleFavorite, updateFavorite } = useFavorites(!!forecast);
  const [editing, setEditing] = useState<Favorite | null>(null);

  const mobile = isMobile();
  const years = useMemo(() => getForecastYears(forecast), [forecast]);

  const rows = useMemo(
    () => (forecast && quotes ? buildStockRows(quotes, forecast, favorites, walletOnly) : []),
    [forecast, quotes, favorites, walletOnly],
  );

  const summary = calculateWalletSummary(rows, quotes?.[BRL_QUOTE_TICKER]?.price);

  const columns = getStockColumns({
    isWallet: walletOnly,
    isMobile: mobile,
    years,
    isFavorite,
    onToggleFavorite: toggleFavorite,
    onEdit: setEditing,
  });

  const showCards = mobile && walletOnly;
  const cardRows = showCards
    ? [...rows].sort((a, b) => parseDividendYield(b.startDY) - parseDividendYield(a.startDY))
    : [];

  return (
    <>
      {walletOnly && (
        <EditPositionModal
          editing={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={(favorite) => {
            updateFavorite(favorite);
            setEditing(null);
          }}
        />
      )}
      <TableContainer sx={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        <Container sx={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', padding: 0 }}>
          {walletOnly && (
            <WalletTotal
              total={summary.totalWallet}
              change={summary.changeWallet}
              allocation={summary.allocation}
              isMobile={mobile}
            />
          )}
          {walletOnly && !!rows.length && (summary.dividendStart || summary.dividendEnd) && (
            <DividendProjectionChart summary={summary} years={years} />
          )}
        </Container>
        <Container maxWidth={"xl"} sx={{ padding: 0 }}>
          {cardRows.map((row) => (
            <StockCard
              key={row.ticker}
              row={row}
              years={years}
              onEdit={() => setEditing(toEditingFavorite(row))}
            />
          ))}
          {!showCards && (
            <DataGrid
              columns={columns}
              rows={rows}
              showToolbar
              loading={!forecast}
              density='compact'
              hideFooter
              initialState={{ sorting: { sortModel: [{ field: 'startDY', sort: 'desc' }] } }}
            />
          )}
        </Container>
      </TableContainer>
    </>
  );
}

export default StockTable;
